import React, { useMemo, useState } from 'react';
import { mvpApi } from '../../../services/api';
import { capitalizeWords, isStrictEmail, normalizeEmail } from '../../../utils/formInputUtils';

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function prettyLanguage(code) {
  const base = String(code || '').split('-')[0].toLowerCase();
  if (base === 'es') return 'Spanish';
  if (base === 'en') return 'English';
  return 'English';
}

function channelLabel(channel) {
  const c = String(channel || '').toLowerCase();
  if (c === 'sms') return 'SMS';
  return 'SMS';
}

function phoneValue(p) {
  if (!p) return '';
  const s = String(p).trim();
  return s.startsWith('+') ? s : `+${s}`;
}

export default function CustomerDetailsCard({ customer, onCustomerUpdated }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');

  const initialForm = useMemo(
    () => ({
      first_name: customer?.first_name || '',
      last_name: customer?.last_name || '',
      middle_name: customer?.middle_name || '',
      phone: phoneValue(customer?.phone),
      email: customer?.email || '',
      preferred_language: String(customer?.preferred_language || 'en').split('-')[0].toLowerCase() === 'es' ? 'es' : 'en',
      preferred_contact_method:
        String(customer?.email_primary) === 'true' || customer?.email_primary === true ? 'email' : 'mobile',
      preferred_contact_channel: channelLabel(customer?.preferred_contact_channel).toLowerCase(),
    }),
    [customer],
  );

  const [form, setForm] = useState(initialForm);

  const hasInvalidEmail = Boolean(normalizeEmail(form.email) && !isStrictEmail(form.email));

  const openEdit = () => {
    setForm(initialForm);
    setError('');
    setEmailError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setForm(initialForm);
    setError('');
    setEmailError('');
    setEditing(false);
  };

  const save = async () => {
    if (!customer?.id) return;
    const normalizedEmail = normalizeEmail(form.email);
    if (normalizedEmail && !isStrictEmail(normalizedEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        first_name: form.first_name.trim() || null,
        last_name: form.last_name.trim() || null,
        middle_name: form.middle_name.trim() || null,
        phone: form.phone.trim() || null,
        email: normalizedEmail || null,
        preferred_language: form.preferred_language,
        preferred_contact_channel: form.preferred_contact_channel,
        email_primary: form.preferred_contact_method === 'email',
      };
      const res = await mvpApi.patchAdminCustomerDetails(customer.id, payload);
      const updated = res?.data?.user;
      if (updated && onCustomerUpdated) onCustomerUpdated(updated);
      setEditing(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not save customer details.');
    } finally {
      setSaving(false);
    }
  };

  const registered = customer?.profile_complete === false ? 'No' : 'Yes';

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 bg-slate-50/60">
        <div className="w-3.5 h-3.5 rounded-[4px] bg-slate-400 shrink-0" />
        <h2 className="text-sm font-semibold text-slate-800">Customer Details</h2>
        {!editing ? (
          <button
            type="button"
            onClick={openEdit}
            className="ml-auto flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-pencil-line text-xs" />
            </span>
            Edit
          </button>
        ) : null}
      </div>

      {error ? (
        <div className="mx-5 mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          {error}
        </div>
      ) : null}

      {!editing ? (
        <div className="px-5 py-4 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            <Field label="First Name" value={customer?.first_name || '-'} />
            <Field label="Last Name" value={customer?.last_name || '-'} />
            <Field label="Second Last Name" value={customer?.middle_name || '-'} />
            <Field label="Customer Since" value={formatDate(customer?.created_at)} />
            <Field label="Registered" value={registered} />
          </div>

          <div className="pt-4 border-t border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Contact Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">Mobile</p>
                <p className="text-sm text-slate-800">{phoneValue(customer?.phone) || '-'}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{channelLabel(customer?.preferred_contact_channel)}</p>
              </div>
              <Field label="Email" value={customer?.email || '-'} />
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Preferences</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              <Field
                label="Contact Method"
                value={customer?.email_primary === true ? 'Email' : 'Mobile'}
              />
              <Field label="Language" value={prettyLanguage(customer?.preferred_language)} />
            </div>
          </div>
        </div>
      ) : (
        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label="First Name" value={form.first_name} onChange={(v) => setForm((f) => ({ ...f, first_name: capitalizeWords(v) }))} />
            <Input label="Last Name" value={form.last_name} onChange={(v) => setForm((f) => ({ ...f, last_name: capitalizeWords(v) }))} />
            <Input label="Second Last Name" value={form.middle_name} onChange={(v) => setForm((f) => ({ ...f, middle_name: v }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Mobile" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
            <Input
              label="Email"
              value={form.email}
              onChange={(v) => {
                setForm((f) => ({ ...f, email: v }));
                const normalized = normalizeEmail(v);
                setEmailError(normalized && !isStrictEmail(normalized) ? 'Please enter a valid email address.' : '');
              }}
              onPaste={(pasted) => {
                const normalized = normalizeEmail(`${form.email}${pasted}`);
                setEmailError(normalized && !isStrictEmail(normalized) ? 'Please enter a valid email address.' : '');
              }}
              type="email"
            />
          </div>
          {emailError ? <p className="text-[11px] text-rose-600">{emailError}</p> : null}
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Channel"
              value={form.preferred_contact_channel}
              onChange={(v) => setForm((f) => ({ ...f, preferred_contact_channel: v }))}
              options={[
                { value: 'sms', label: 'SMS' },
              ]}
            />
            <Select
              label="Contact Method"
              value={form.preferred_contact_method}
              onChange={(v) => setForm((f) => ({ ...f, preferred_contact_method: v }))}
              options={[
                { value: 'mobile', label: 'Mobile' },
                { value: 'email', label: 'Email' },
              ]}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Language"
              value={form.preferred_language}
              onChange={(v) => setForm((f) => ({ ...f, preferred_language: v }))}
              options={[
                { value: 'en', label: 'English' },
                { value: 'es', label: 'Spanish' },
              ]}
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              disabled={saving || hasInvalidEmail}
              onClick={save}
              className="text-xs font-semibold px-4 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 transition-colors cursor-pointer"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={cancelEdit}
              className="text-xs font-medium px-4 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-60 transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-sm text-slate-800 break-words">{value || '-'}</p>
    </div>
  );
}

function Input({ label, value, onChange, onPaste, type = 'text' }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onPaste={(e) => onPaste?.(e.clipboardData?.getData('text') || '')}
        className="w-full h-10 px-3 text-sm border border-slate-200 rounded-lg text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
      />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 px-3 text-sm border border-slate-200 rounded-lg text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
